import './style.scss'
import { Button, Drawer, Input, message, Modal, Select } from 'antd'
import { RcFile } from 'antd/es/upload'
import Dragger from 'antd/es/upload/Dragger'
import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsApi } from '@/api'
import * as Api from '@/api/home'
import IconPictureEmpty from '@/assets/icon-picture-empty.png'
import { IconRandom } from '@/assets/svg/icon-random'
import { IconScore } from '@/assets/svg/icon-score'
import { IconUpload } from '@/assets/svg/icon-upload'
import { UserStore } from '@/global-states'
import { checkSensitive, getCreateFaceRandomTexts, showPlanModal, urlOsskey } from '@/libs/util'
import { DeleteOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons'
import AvatarFormModal from '../FormModal'

interface IProps {
  open: boolean
  onCancel?: () => void
  onOk?: () => void
}

const CreateAiAvatarNewDrawer: FC<IProps> = (props) => {
  const { uid } = UserStore
  const { open, onCancel, onOk } = props
  const { t } = useTranslation(['system', 'avatar'])
  const [nextCost, setNextCost] = useState<any>()
  const [ages, setAges] = useState<any[]>([])
  const [genders, setGenders] = useState<any[]>([])
  const [nationals, setNationals] = useState<any[]>([])
  const [results, setResults] = useState<any[]>([])
  const [form, setForm] = useState<any>()
  const [loading, setLoading] = useState(false)
  const timerRef = useRef<any>()
  const [formModalOpen, setFormModalOpen] = useState(false)
  const [selectAvatar, setSelectAvatar] = useState<any>()
  const [photoInfo, setPhotoInfo] = useState<any>()
  const [uploadling, setUploadling] = useState(false)
  const [randomKey, setRandomKey] = useState<any>()

  useEffect(() => {
    if (open) {
      getNextCost()
      getFormTags()
      UserStore.initSensitiveWords()

      const postData = JSON.parse(localStorage.getItem('face_post_data') || '{}')
      if (postData?.uid === uid) {
        initData()
      } else {
        setResults([])
        setPhotoInfo(undefined)
        setLoading(false)
        setUploadling(false)
      }
    } else {
      clearEvent()
    }
  }, [open])

  const initData = () => {
    const taskId = localStorage.getItem('face_task_id')
    const postData = localStorage.getItem('face_post_data')

    if (taskId) {
      clearEvent()
      loopRes(taskId)
      timerRef.current = setInterval(() => {
        loopRes(taskId)
      }, 3000)
    }

    if (postData) {
      const parseData = JSON.parse(postData || '{}')
      setForm(parseData)
      setPhotoInfo({
        oss_key: parseData?.reference_image_oss_key
      })
    }
  }

  const getNextCost = async () => {
    const res = await Api.getFaceNextCost()
    setNextCost(res || {})
    return res
  }

  const getFormTags = async () => {
    const [r1, r2, r3] = await Promise.all([Api.getAgeTags(), Api.getGenderTags(), Api.getNationalTags()])
    const postData = JSON.parse(localStorage.getItem('face_post_data') || '{}')
    setAges(r1?.tags || [])
    setGenders(r2?.tags || [])
    setNationals(r3?.tags || [])
    if (postData?.id !== uid) {
      setForm({
        age_group: r1?.tags?.[0].value,
        gender: r2?.tags?.[1].value,
        nationality: r3?.tags?.[0].value,
        detail_prompt: getRandomText(2)
      })
    }
  }

  const onFormChange = (key: string, e: any) => {
    if (key === 'gender') {
      const text = getRandomText(e === genders[0] ? 1 : 2)
      setForm({
        ...form,
        [key]: e,
        detail_prompt: text
      })
    } else {
      setForm({
        ...form,
        [key]: e?.target ? e.target.value : e
      })
    }
  }

  const clearEvent = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
  }

  const setRandomText = () => {
    setForm({
      ...form,
      detail_prompt: getRandomText(form.gender === genders[0] ? 1 : 2)
    })
  }

  const getRandomText = (gender: number) => {
    const r: any = getCreateFaceRandomTexts().filter((t) => t.key !== randomKey && t.gender === gender)
    const index = Math.floor(Math.random() * r.length)
    setRandomKey(r[index].key)
    return r[index].text
  }

  const loopRes = async (task_id: string) => {
    const res = await Api.loopFaceFusionTask(task_id)
    if (res.status === 2) {
      setLoading(false)
      clearEvent()
      setResults([])
      message.error(t('avatar.generate_failed'))
    }

    if (res.status === 3) {
      clearEvent()
      setLoading(false)
      setResults(res?.images || [])
    }
  }

  const onSubmit = async () => {
    const { detail_prompt } = form
    if (detail_prompt?.trim()) {
      const errorWords = checkSensitive([{ words: detail_prompt }])

      if (errorWords?.length) {
        return message.error(
          <div style={{ padding: '12px' }}>
            {t('avatar.sensitive_words_warning')}
            <label className="red">（{errorWords.join('、')}</label>），{t('avatar.modify_and_retry')}
          </div>
        )
      }
    }

    try {
      const postData = {
        ...form,
        reference_image_oss_key: photoInfo?.oss_key
      }
      setLoading(true)
      localStorage.setItem(
        'face_post_data',
        JSON.stringify({
          ...postData,
          uid
        })
      )
      localStorage.removeItem('face_task_id')
      setResults([{}, {}, {}, {}])
      const res = await Api.submitFaceFusionTask(postData)
      getNextCost()
      clearEvent()
      localStorage.setItem('face_task_id', res.task_id)
      timerRef.current = setInterval(() => {
        loopRes(res.task_id)
      }, 3000)
    } catch {
      setLoading(false)
    }
  }

  const onSelect = (avatar: any) => {
    setSelectAvatar(avatar)
    setFormModalOpen(true)
    onCancel?.()
  }

  const completeCreate = async (form?: any) => {
    try {
      const params: any = {
        oss_key: form?.face_oss_key,
        source_type: 3,
        with_new_group: {
          ...form
        }
      }

      await Api.addDigitalHumans(params)
      message.success(t('system.submit_success'))
      setFormModalOpen(false)
      onCancel?.()
      onOk?.()
    } catch (error: any) {
      if (error.code === 1014) {
        Modal.confirm({
          title: t('avatar.limit_reached'),
          content: <div>{t('avatar.limit_content')}</div>,
          okText: t('avatar.upgrade_membership'),
          cancelText: t('system.cancel'),
          onOk: () => {
            showPlanModal()
          }
        })
      }
    }
  }

  const onDrop = async (file: RcFile) => {
    const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
    if (!['png', 'jpg', 'jpeg'].includes(type)) {
      message.warning(t('avatar.photo_format_warning'))
    }
  }

  const beforeUpload = async (file: RcFile) => {
    return new Promise((resolve) => {
      const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
      if (['png', 'jpg', 'jpeg'].includes(type)) {
        const size = file?.size || 0
        if (size / 1000 / 1000 > 50) {
          message.warning(t('avatar.photo_size_warning'))
          resolve(false)
        }
      } else {
        message.warning(t('avatar.photo_format_warning'))
        resolve(false)
      }
      resolve(true)
    })
  }

  // media_type -  0 未定义  1 视频  2 音频 3 图片
  const uploadFile = async (file: RcFile, media_type: 0 | 1 | 2 | 3) => {
    try {
      setUploadling(true)
      const segs = (file.name || '').split(/\./)
      const { upload_url, oss_key, content_type } =
        (await cmsApi.post('upload_url', {
          extension: segs[segs.length - 1],
          media_type
        })) || {}
      if (!upload_url) {
        throw new Error(t('system.upload_failed'))
      }

      await cmsApi.upload(upload_url.replace(/^http:\/\//, 'https://').replace('-internal', ''), file, {
        headers: {
          'Content-Type': content_type
        }
      })

      setPhotoInfo({
        oss_key,
        file
      })
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        message.error(err?.message || err)
      }
    } finally {
      setUploadling(false)
    }
  }

  return (
    <Drawer
      className="create-ai-new-drawer"
      open={open}
      title={t('avatar.ai_generated_avatar')}
      width={1000}
      onClose={onCancel}
      footer={null}
    >
      <div className="drawer-wrapper">
        <div className="wrapper-left">
          <div className="form">
            <div className="form-item">
              <div className="title">{t('avatar.age')}</div>
              <div className="content">
                <Select
                  fieldNames={{
                    label: 'display_name'
                  }}
                  value={form?.age_group}
                  options={ages}
                  onChange={onFormChange.bind(this, 'age_group')}
                ></Select>
              </div>
            </div>

            <div className="form-item">
              <div className="title">{t('avatar.gender')}</div>
              <div className="content">
                <Select
                  fieldNames={{
                    label: 'display_name'
                  }}
                  options={genders}
                  value={form?.gender}
                  onChange={onFormChange.bind(this, 'gender')}
                ></Select>
              </div>
            </div>

            <div className="form-item">
              <div className="title">{t('avatar.region')}</div>
              <div className="content">
                <Select
                  fieldNames={{
                    label: 'display_name'
                  }}
                  value={form?.nationality}
                  options={nationals}
                  onChange={onFormChange.bind(this, 'nationality')}
                ></Select>
              </div>
            </div>

            <div className="form-item">
              <div className="title">{t('avatar.face_details')}</div>
              <div className="content">
                <div className="textarea-box">
                  <Input.TextArea
                    value={form?.detail_prompt}
                    onChange={onFormChange.bind(this, 'detail_prompt')}
                    autoFocus
                    placeholder={t('avatar.input_face_details')}
                    showCount
                    maxLength={300}
                  ></Input.TextArea>
                  <Button icon={<IconRandom />} className="btn" type="primary" onClick={setRandomText}>
                    {t('avatar.random_generate')}
                  </Button>
                </div>
              </div>
            </div>

            <div className="form-item">
              <div className="title">{t('avatar.upload_reference_image')}</div>
              <div className="form-tip">{t('avatar.reference_image_tip')}</div>
              <div className="content">
                {photoInfo?.oss_key ? (
                  <div className="img-box">
                    <img src={urlOsskey(photoInfo.oss_key)} />
                    <div className="delete-box" onClick={() => setPhotoInfo(undefined)}>
                      <DeleteOutlined />
                    </div>
                  </div>
                ) : (
                  <Dragger
                    accept=".png,.PNG,.jpg,.JPG,.jepg,.JPEG"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      const flag = (await beforeUpload(file)) as any
                      if (flag) {
                        uploadFile(file, 3)
                      }
                      return flag
                    }}
                    onDrop={(e) => onDrop(e.dataTransfer.files?.[0] as any)}
                  >
                    {uploadling ? (
                      <>
                        <div className="uploading">
                          <LoadingOutlined />
                          <div>{t('avatar.uploading')}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="ant-upload-drag-icon">
                          <IconUpload />
                        </p>
                        <p className="ant-upload-text">{t('avatar.click_to_upload')}</p>
                      </>
                    )}
                  </Dragger>
                )}
              </div>
            </div>
          </div>

          <div className="footer">
            <Button type="primary" loading={loading} onClick={onSubmit}>
              {loading ? (
                <span>{t('avatar.generating')}</span>
              ) : results?.length ? (
                <span>
                  <ReloadOutlined />
                  {t('avatar.regenerate')}
                </span>
              ) : (
                <span>{t('avatar.generate_avatar')}</span>
              )}

              {nextCost?.is_free && <div className="tag">{t('avatar.limited_free')}</div>}

              {!nextCost?.is_free && !loading && (
                <span className="cost">
                  <IconScore className="icon-score" />
                  <label>
                    {nextCost?.credits}
                    {t('avatar.points')}
                  </label>
                </span>
              )}
            </Button>
          </div>
        </div>
        <div className="wrapper-right">
          {results?.length ? (
            <>
              <div className="title">{t('avatar.select_face_prompt')}</div>
              <div className="img-list">
                {results.map((r, index) => (
                  <div className="list-item" key={index}>
                    <div className="img-box">
                      {r?.url ? (
                        <>
                          <img src={r.url} />
                          <Button className="select-btn" type="primary" onClick={onSelect.bind(this, r)}>
                            {t('avatar.select_and_create')}
                          </Button>
                          <div className="mask"></div>
                        </>
                      ) : (
                        <div className="loading">
                          <LoadingOutlined />
                          <div className="loading-text">{t('avatar.generating')}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className={`empty`}>
              <img src={IconPictureEmpty} />
              <div className="desc">{t('avatar.generate_prompt')}</div>
            </div>
          )}
        </div>
      </div>

      <AvatarFormModal
        open={formModalOpen}
        onCancel={() => setFormModalOpen(false)}
        onSuccess={completeCreate}
        avatar={selectAvatar}
      />
    </Drawer>
  )
}

export default CreateAiAvatarNewDrawer
